import { Helmet } from "react-helmet-async";
import { LANGS } from "@/lib/i18nStrings";

const DEFAULT_IMG = "https://images.unsplash.com/photo-1546412414-8035e1776c9a?w=1600";
const SITE = process.env.REACT_APP_BACKEND_URL || "";

// Google recommends BCP-47 codes. Our internal short codes map cleanly:
//   ar → ar (also emit ar-AE for the UAE audience so hreflang matches locale)
//   ru → ru      zh → zh-CN     fr → fr
//   ja → ja      ko → ko        en → en
const HREFLANG_MAP = {
  en: ["en"],
  ar: ["ar", "ar-AE"],
  ru: ["ru"],
  zh: ["zh-CN"],
  fr: ["fr"],
  ja: ["ja"],
  ko: ["ko"],
};

function buildAltUrl(basePath, code) {
  // English is the default (no query param) so canonical stays clean.
  if (code === "en") return `${SITE}${basePath}`;
  const sep = basePath.includes("?") ? "&" : "?";
  return `${SITE}${basePath}${sep}lang=${code}`;
}

export default function SEO({
  title, description, path = "/", image = DEFAULT_IMG,
  type = "website", noIndex = false, schema,
}) {
  // Strip any existing ?lang= from the incoming path so alternates are clean.
  const cleanPath = (path || "/").replace(/([?&])lang=[^&]*&?/, "$1").replace(/[?&]$/, "");
  const canonical = `${SITE}${cleanPath}`;
  const fullTitle = title ? `${title} · Point Penta` : "Point Penta Real Estate";

  const alternates = [];
  for (const l of LANGS) {
    const url = buildAltUrl(cleanPath, l.code);
    for (const tag of HREFLANG_MAP[l.code] || [l.code]) {
      alternates.push({ hreflang: tag, href: url });
    }
  }
  // x-default → English variant, per Google guidance.
  alternates.push({ hreflang: "x-default", href: buildAltUrl(cleanPath, "en") });

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* hreflang alternates — one per supported language + x-default */}
      {alternates.map((a) => (
        <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={a.href} />
      ))}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Point Penta Real Estate" />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {schema && (Array.isArray(schema) ? schema : [schema]).map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}
